import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalApi, employeeApi, departmentApi, cycleApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, StatCard, EmptyState, Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  FlagIcon,
  FireIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  TrashIcon,
  BoltIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';

const LEVEL_STYLES = {
  company:    'bg-purple-100 text-purple-700 border-purple-200',
  department: 'bg-blue-100 text-blue-700 border-blue-200',
  team:       'bg-indigo-100 text-indigo-700 border-indigo-200',
  individual: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_STYLES = {
  not_started: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  on_track:    'bg-green-100 text-green-700',
  at_risk:     'bg-amber-100 text-amber-700',
  off_track:   'bg-red-100 text-red-700',
  completed:   'bg-emerald-100 text-emerald-700',
  cancelled:   'bg-gray-200 text-gray-600',
};

const HEALTH_BORDER = {
  healthy:  'border-l-4 border-green-500',
  warning:  'border-l-4 border-amber-500',
  critical: 'border-l-4 border-red-500',
};

function LevelBadge({ level }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border capitalize', LEVEL_STYLES[level] || LEVEL_STYLES.individual)}>
      {level}
    </span>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium capitalize', STATUS_STYLES[status] || STATUS_STYLES.in_progress)}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function ConfidenceBadge({ score }) {
  if (!score) return null;
  const color = score >= 4 ? 'bg-green-100 text-green-700' : score >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium', color)}>
      <HeartIcon className="w-3 h-3" />
      {score}/5
    </span>
  );
}

function ProgressBar({ value = 0, size = 'md' }) {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : pct >= 25 ? 'bg-amber-500' : 'bg-red-500';
  const h = size === 'lg' ? 'h-3' : 'h-2';
  return (
    <div className={clsx('w-full bg-gray-100 rounded-full overflow-hidden', h)}>
      <div className={clsx(h, color, 'rounded-full transition-all')} style={{ width: `${pct}%` }} />
    </div>
  );
}

function formatKRValue(kr) {
  const cur = kr.current_value ?? 0;
  const tgt = kr.target_value ?? 0;
  if (kr.type === 'currency') return `${kr.unit || '$'}${cur} / ${kr.unit || '$'}${tgt}`;
  if (kr.type === 'percentage') return `${cur}% / ${tgt}%`;
  return `${cur}${kr.unit ? ' ' + kr.unit : ''} / ${tgt}${kr.unit ? ' ' + kr.unit : ''}`;
}

function NewGoalForm({ onSubmit, onClose, employees = [], cycles = [], departments = [], defaultEmployeeId }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    level: 'individual',
    type: 'outcome',
    employee_id: defaultEmployeeId || '',
    department_id: '',
    cycle_id: '',
    due_date: '',
  });
  const valid = form.title.trim().length >= 3 && form.level && form.type;
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Title</label>
        <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Launch new onboarding flow" />
      </div>
      <div>
        <label className="label">Description</label>
        <textarea className="input h-20 resize-none" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Why does this goal matter?" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Level</label>
          <select className="input" value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))}>
            <option value="company">Company</option>
            <option value="department">Department</option>
            <option value="team">Team</option>
            <option value="individual">Individual</option>
          </select>
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="outcome">Outcome</option>
            <option value="activity">Activity</option>
            <option value="learning">Learning</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Owner</label>
          <select className="input" value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}>
            <option value="">Unassigned</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Department</label>
          <select className="input" value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
            <option value="">—</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Cycle</label>
          <select className="input" value={form.cycle_id} onChange={e => setForm(f => ({ ...f, cycle_id: e.target.value }))}>
            <option value="">—</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Due date</label>
          <input type="date" className="input" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid} onClick={() => onSubmit(form)} className="btn-primary disabled:opacity-40">Create goal</button>
      </div>
    </div>
  );
}

function KeyResultForm({ onSubmit, onClose }) {
  const [form, setForm] = useState({ title: '', type: 'numeric', target_value: '', unit: '', weight: 1 });
  const valid = form.title.trim().length >= 3 && form.target_value !== '';
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Key result</label>
        <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Reach 95% CSAT" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Type</label>
          <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="numeric">Numeric</option>
            <option value="percentage">Percentage</option>
            <option value="milestone">Milestone</option>
            <option value="currency">Currency</option>
          </select>
        </div>
        <div>
          <label className="label">Target value</label>
          <input className="input" type="number" value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Unit</label>
          <input className="input" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="%, $, users…" />
        </div>
        <div>
          <label className="label">Weight</label>
          <input className="input" type="number" step="0.1" min="0.1" max="10" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: parseFloat(e.target.value) || 1 }))} />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid} onClick={() => onSubmit({ ...form, target_value: parseFloat(form.target_value) })} className="btn-primary disabled:opacity-40">Add key result</button>
      </div>
    </div>
  );
}

function KRUpdateForm({ kr, onSubmit, onClose }) {
  const [current, setCurrent] = useState(kr.current_value ?? 0);
  const [status, setStatus]   = useState(kr.status || 'in_progress');
  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-sm font-medium text-gray-800">{kr.title}</p>
        <p className="text-xs text-gray-500 mt-0.5">Target: {kr.target_value}{kr.unit ? ' ' + kr.unit : ''}</p>
      </div>
      <div>
        <label className="label">Current value</label>
        <input className="input" type="number" value={current} onChange={e => setCurrent(e.target.value)} />
      </div>
      <div>
        <label className="label">Status</label>
        <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="not_started">Not started</option>
          <option value="in_progress">In progress</option>
          <option value="on_track">On track</option>
          <option value="at_risk">At risk</option>
          <option value="off_track">Off track</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={() => onSubmit({ current_value: parseFloat(current) || 0, status })} className="btn-primary">Save update</button>
      </div>
    </div>
  );
}

function GoalUpdateForm({ goal, onSubmit, onClose }) {
  const [progress, setProgress]     = useState(goal.progress_pct ?? 0);
  const [confidence, setConfidence] = useState(goal.confidence_score ?? 3);
  const [status, setStatus]         = useState(goal.status || 'in_progress');
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Progress ({progress}%)</label>
        <input type="range" min="0" max="100" value={progress} onChange={e => setProgress(parseInt(e.target.value))} className="w-full" />
      </div>
      <div>
        <label className="label">Confidence</label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => setConfidence(n)}
              className={clsx('flex-1 py-2 rounded-lg text-sm font-medium border transition-colors', confidence === n ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50')}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label">Status</label>
        <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="not_started">Not started</option>
          <option value="in_progress">In progress</option>
          <option value="on_track">On track</option>
          <option value="at_risk">At risk</option>
          <option value="off_track">Off track</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={() => onSubmit({ progress_pct: progress, confidence_score: confidence, status })} className="btn-primary">Save</button>
      </div>
    </div>
  );
}

function GoalCard({ goal, onAddKR, onUpdateKR, onUpdateGoal, onDelete, canManage }) {
  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <LevelBadge level={goal.level} />
              <StatusChip status={goal.status} />
              <ConfidenceBadge score={goal.confidence_score} />
              {goal.cycle?.name && <span className="text-[11px] text-gray-500">· {goal.cycle.name}</span>}
            </div>
            <h3 className="text-base font-semibold text-gray-900 truncate">{goal.title}</h3>
            {goal.description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{goal.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {goal.due_date && <span className="text-xs text-gray-500 whitespace-nowrap">Due {new Date(goal.due_date).toLocaleDateString()}</span>}
            {canManage && (
              <>
                <button onClick={() => onUpdateGoal(goal)} className="p-1.5 hover:bg-gray-100 rounded" title="Update progress">
                  <PencilSquareIcon className="w-4 h-4 text-gray-500" />
                </button>
                <button onClick={() => onDelete(goal)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="card-body space-y-4">
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-medium text-gray-700">Progress</span>
            <span className="font-semibold text-gray-900">{Math.round(goal.progress_pct ?? 0)}%</span>
          </div>
          <ProgressBar value={goal.progress_pct} size="lg" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Key results</p>
            {canManage && (
              <button onClick={() => onAddKR(goal)} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                <PlusIcon className="w-3 h-3" /> Add key result
              </button>
            )}
          </div>
          {(goal.key_results || []).length === 0 ? (
            <p className="text-xs text-gray-400 italic">No key results yet</p>
          ) : (
            <ul className="space-y-2">
              {(goal.key_results || []).map(kr => (
                <li key={kr.id} className="flex items-center gap-3 py-2 border-t border-gray-100 first:border-t-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">{kr.title}</p>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">{formatKRValue(kr)}</p>
                  </div>
                  <StatusChip status={kr.status} />
                  {canManage && (
                    <button onClick={() => onUpdateKR(goal, kr)} className="text-xs text-blue-600 hover:underline">
                      Update
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgGoalRow({ goal, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const { data: detail } = useQuery({
    queryKey: ['goal-detail', goal.id],
    queryFn:  () => goalApi.get(goal.id).then(r => r.data),
    enabled:  open,
  });
  const children = detail?.children || detail?.child_goals || [];

  return (
    <div>
      <div className="flex items-center gap-3 py-3 px-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
           style={{ paddingLeft: `${12 + depth * 24}px` }}>
        <button onClick={() => setOpen(o => !o)} className="p-0.5 hover:bg-gray-200 rounded">
          {open ? <ChevronDownIcon className="w-4 h-4 text-gray-500" /> : <ChevronRightIcon className="w-4 h-4 text-gray-500" />}
        </button>
        <LevelBadge level={goal.level} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{goal.title}</p>
          <p className="text-xs text-gray-500 truncate">
            {goal.owner?.name || goal.employee?.name || 'Unassigned'}
            {goal.department?.name && ` · ${goal.department.name}`}
          </p>
        </div>
        <div className="w-40">
          <ProgressBar value={goal.progress_pct} />
        </div>
        <span className="text-xs font-mono text-gray-700 w-10 text-right">{Math.round(goal.progress_pct ?? 0)}%</span>
        <StatusChip status={goal.status} />
      </div>
      {open && children.length > 0 && (
        <div>
          {children.map(c => <OrgGoalRow key={c.id} goal={c} depth={depth + 1} />)}
        </div>
      )}
      {open && children.length === 0 && detail && (
        <p className="text-xs text-gray-400 italic py-2" style={{ paddingLeft: `${44 + depth * 24}px` }}>No sub-goals</p>
      )}
    </div>
  );
}

export default function GoalsDashboard() {
  const { user } = useAuth();
  const qc       = useQueryClient();
  const [tab, setTab]                   = useState('mine'); // mine | org | heatmap
  const [showNewGoal, setShowNewGoal]   = useState(false);
  const [addingKRto, setAddingKRto]     = useState(null);
  const [updatingKR, setUpdatingKR]     = useState(null); // { goal, kr }
  const [updatingGoal, setUpdatingGoal] = useState(null);
  const [orgFilters, setOrgFilters]     = useState({ level: '', department_id: '' });

  const employeeId = user?.employee_id || user?.id;

  const { data: myGoals = [], isLoading: loadingMine } = useQuery({
    queryKey: ['my-goals', employeeId],
    queryFn:  () => goalApi.list({ employee_id: employeeId }).then(r => r.data?.data || r.data || []),
    enabled:  !!employeeId,
  });

  const { data: orgGoals = [], isLoading: loadingOrg } = useQuery({
    queryKey: ['org-goals', orgFilters],
    queryFn:  () => goalApi.list(orgFilters).then(r => r.data?.data || r.data || []),
    enabled:  tab === 'org',
  });

  const { data: heatmap = [], isLoading: loadingHeat } = useQuery({
    queryKey: ['goals-heatmap'],
    queryFn:  () => goalApi.heatmap({}).then(r => r.data?.data || r.data || []),
    enabled:  tab === 'heatmap',
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-lite'],
    queryFn:  () => employeeApi.list().then(r => r.data?.data || r.data || []),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data?.data || r.data || []),
  });
  const { data: cycles = [] } = useQuery({
    queryKey: ['cycles'],
    queryFn:  () => cycleApi.list().then(r => r.data?.data || r.data || []),
  });

  const createMut = useMutation({
    mutationFn: (data) => goalApi.create(data),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['my-goals'] });
      qc.invalidateQueries({ queryKey: ['org-goals'] });
      setShowNewGoal(false);
      toast.success('Goal created');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const addKRMut = useMutation({
    mutationFn: ({ id, data }) => goalApi.addKeyResult(id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-goals'] }); setAddingKRto(null); toast.success('Key result added'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const updateKRMut = useMutation({
    mutationFn: ({ id, krid, data }) => goalApi.updateKeyResult(id, krid, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-goals'] }); setUpdatingKR(null); toast.success('Key result updated'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const updateGoalMut = useMutation({
    mutationFn: ({ id, data }) => goalApi.update(id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-goals'] }); setUpdatingGoal(null); toast.success('Goal updated'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => goalApi.delete(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-goals'] }); toast.success('Goal deleted'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const stats = useMemo(() => {
    const active = myGoals.filter(g => g.status !== 'completed' && g.status !== 'cancelled');
    const avg = myGoals.length
      ? Math.round(myGoals.reduce((s, g) => s + (g.progress_pct || 0), 0) / myGoals.length)
      : 0;
    const atRisk = myGoals.filter(g => g.status === 'at_risk' || g.status === 'off_track').length;
    const completed = myGoals.filter(g => g.status === 'completed').length;
    return { active: active.length, avg, atRisk, completed };
  }, [myGoals]);

  const filteredOrg = useMemo(() => {
    let list = orgGoals;
    if (orgFilters.level)         list = list.filter(g => g.level === orgFilters.level);
    if (orgFilters.department_id) list = list.filter(g => String(g.department_id) === String(orgFilters.department_id));
    return list;
  }, [orgGoals, orgFilters]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Goals & OKRs"
        subtitle="Track outcomes, align teams, and own your growth."
        actions={
          <button onClick={() => setShowNewGoal(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" /> New goal
          </button>
        }
      />

      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Active goals"  value={stats.active}    color="blue"   icon={ChartBarIcon} />
        <StatCard title="Avg progress"  value={`${stats.avg}%`} color="purple" icon={BoltIcon} />
        <StatCard title="At risk"       value={stats.atRisk}    color="amber"  icon={FireIcon} />
        <StatCard title="Completed"     value={stats.completed} color="green"  icon={CheckCircleIcon} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {[
          ['mine',    'My goals'],
          ['org',     'Team / Org'],
          ['heatmap', 'Health heatmap'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {label}
          </button>
        ))}
      </div>

      {/* My goals */}
      {tab === 'mine' && (
        loadingMine ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : myGoals.length === 0 ? (
          <EmptyState
            title="No goals yet"
            description="Create your first goal to start tracking outcomes and key results."
            action={<button className="btn-primary" onClick={() => setShowNewGoal(true)}><PlusIcon className="w-4 h-4" /> Create goal</button>}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {myGoals.map(goal => (
              <GoalCard key={goal.id}
                goal={goal}
                canManage
                onAddKR={setAddingKRto}
                onUpdateKR={(g, kr) => setUpdatingKR({ goal: g, kr })}
                onUpdateGoal={setUpdatingGoal}
                onDelete={(g) => {
                  if (window.confirm(`Delete "${g.title}"? This cannot be undone.`)) deleteMut.mutate(g.id);
                }}
              />
            ))}
          </div>
        )
      )}

      {/* Team / Org */}
      {tab === 'org' && (
        <div className="space-y-4">
          <div className="card p-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="label">Level</label>
              <select className="input w-44 text-sm" value={orgFilters.level} onChange={e => setOrgFilters(f => ({ ...f, level: e.target.value }))}>
                <option value="">All levels</option>
                <option value="company">Company</option>
                <option value="department">Department</option>
                <option value="team">Team</option>
                <option value="individual">Individual</option>
              </select>
            </div>
            <div>
              <label className="label">Department</label>
              <select className="input w-52 text-sm" value={orgFilters.department_id} onChange={e => setOrgFilters(f => ({ ...f, department_id: e.target.value }))}>
                <option value="">All departments</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            {(orgFilters.level || orgFilters.department_id) && (
              <button onClick={() => setOrgFilters({ level: '', department_id: '' })} className="text-xs text-blue-600 hover:underline">
                Clear filters
              </button>
            )}
          </div>

          {loadingOrg ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
          : filteredOrg.length === 0 ? (
            <EmptyState title="No goals match" description="Try adjusting filters or broadening the scope." />
          ) : (
            <div className="card overflow-hidden">
              <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <span className="w-5" />
                <span className="w-[88px]">Level</span>
                <span className="flex-1">Title / Owner</span>
                <span className="w-40">Progress</span>
                <span className="w-10 text-right">%</span>
                <span className="w-24">Status</span>
              </div>
              {filteredOrg.map(g => <OrgGoalRow key={g.id} goal={g} />)}
            </div>
          )}
        </div>
      )}

      {/* Heatmap */}
      {tab === 'heatmap' && (
        loadingHeat ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : heatmap.length === 0 ? (
          <EmptyState title="No data yet" description="Heatmap populates once departments have active goals." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {heatmap.map(d => (
              <div key={d.department_id} className={clsx('card p-5', HEALTH_BORDER[d.health] || HEALTH_BORDER.healthy)}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{d.department_name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{d.goal_count} goal{d.goal_count === 1 ? '' : 's'}</p>
                  </div>
                  <span className={clsx(
                    'text-[11px] font-semibold uppercase px-2 py-0.5 rounded',
                    d.health === 'healthy' ? 'bg-green-100 text-green-700'
                      : d.health === 'warning' ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                  )}>{d.health}</span>
                </div>
                <p className="text-4xl font-bold text-gray-900 tabular-nums">
                  {Math.round(d.avg_progress_pct ?? 0)}<span className="text-xl text-gray-400">%</span>
                </p>
                <p className="text-xs text-gray-500 mb-3">Average progress</p>
                <ProgressBar value={d.avg_progress_pct} size="lg" />
                <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-[11px] uppercase text-gray-500 tracking-wide">Confidence</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {(d.avg_confidence ?? 0).toFixed(1)}<span className="text-xs text-gray-400">/5</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-gray-500 tracking-wide flex items-center gap-1">
                      <FlagIcon className="w-3 h-3" /> At risk
                    </p>
                    <p className={clsx('text-lg font-semibold', d.at_risk_count > 0 ? 'text-red-600' : 'text-gray-900')}>
                      {d.at_risk_count}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Modals */}
      <Modal open={showNewGoal} onClose={() => setShowNewGoal(false)} title="New goal" size="md">
        <NewGoalForm
          employees={employees}
          cycles={cycles}
          departments={departments}
          defaultEmployeeId={employeeId}
          onSubmit={(data) => createMut.mutate(data)}
          onClose={() => setShowNewGoal(false)}
        />
      </Modal>

      <Modal open={!!addingKRto} onClose={() => setAddingKRto(null)} title={`Add key result — ${addingKRto?.title || ''}`} size="md">
        {addingKRto && (
          <KeyResultForm
            onSubmit={(data) => addKRMut.mutate({ id: addingKRto.id, data })}
            onClose={() => setAddingKRto(null)}
          />
        )}
      </Modal>

      <Modal open={!!updatingKR} onClose={() => setUpdatingKR(null)} title="Update key result" size="md">
        {updatingKR && (
          <KRUpdateForm
            kr={updatingKR.kr}
            onSubmit={(data) => updateKRMut.mutate({ id: updatingKR.goal.id, krid: updatingKR.kr.id, data })}
            onClose={() => setUpdatingKR(null)}
          />
        )}
      </Modal>

      <Modal open={!!updatingGoal} onClose={() => setUpdatingGoal(null)} title={`Update — ${updatingGoal?.title || ''}`} size="md">
        {updatingGoal && (
          <GoalUpdateForm
            goal={updatingGoal}
            onSubmit={(data) => updateGoalMut.mutate({ id: updatingGoal.id, data })}
            onClose={() => setUpdatingGoal(null)}
          />
        )}
      </Modal>
    </div>
  );
}
